import React from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { getApiErrorMessage } from "../api/client";
import type { TenantInfo } from "../api/types";
import { useAuth } from "../auth/AuthContext";
import { AuthShell } from "../components/AuthShell";
import {
  authInputClassName,
  Field,
  FormError,
  PasswordInput,
  SubmitButton,
} from "../components/FormControls";
import { Input } from "../components/ui";

export function LoginPage({ tenant }: { tenant: TenantInfo }) {
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");
  const resetDone = new URLSearchParams(location.search).get("reset") === "ok";

  if (auth.status === "authenticated") return <Navigate to="/" replace />;
  if (auth.status === "selecting") {
    return <Navigate to="/select-supplier" replace />;
  }

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setLoading(true);
    setError("");
    try {
      const result = await auth.login(email.trim(), password);
      navigate(result.requiresSupplierSelection ? "/select-supplier" : "/", {
        replace: true,
      });
    } catch (requestError) {
      setError(
        getApiErrorMessage(
          requestError,
          "E-mail ou senha inválidos. Verifique os dados e tente novamente."
        )
      );
    } finally {
      setLoading(false);
    }
  }

  return (
    <AuthShell
      tenant={tenant}
      title="Portal do fornecedor"
      description="Entre para acompanhar documentos e pendências da sua empresa."
      footer={
        <span>
          Precisa de acesso? Entre em contato com a empresa que enviou seu
          convite.
        </span>
      }
    >
      <form className="space-y-4" onSubmit={submit}>
        {resetDone && (
          <div
            role="status"
            className="rounded-xl border border-teal-200 bg-teal-50 px-4 py-3 text-sm text-teal-800"
          >
            Senha alterada. Entre novamente com sua nova senha.
          </div>
        )}
        {error && <FormError message={error} />}
        <Field label="E-mail">
          <Input
            className={authInputClassName}
            type="email"
            autoComplete="username"
            autoFocus
            required
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            placeholder="voce@empresa.com.br"
          />
        </Field>
        <Field label="Senha">
          <PasswordInput
            autoComplete="current-password"
            required
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            placeholder="Digite sua senha"
          />
        </Field>
        <SubmitButton
          loading={loading}
          disabled={!email.trim() || !password}
          className="mt-2"
        >
          Entrar
        </SubmitButton>
      </form>
    </AuthShell>
  );
}
